/**
 * Who is told when this server's outbound address moves.
 *
 * The alert fires once per new address, and the address changes on every
 * redeploy - so an unwanted recipient is a recurring cost in Resend credits,
 * not a one-off. There is no default and no fallback: an empty list means
 * nobody is emailed, and this panel says so plainly.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, Plus, X } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_RECIPIENTS = 5;

const errorOf = (err, fallback) =>
  err?.response?.data?.detail?.error || err?.response?.data?.detail || fallback;

const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const EgressAlertRecipients = ({ isRTL }) => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/otp/egress-alert`);
      setSettings(response.data);
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر التحميل' : 'Could not load the recipient list'));
    }
  }, [isRTL]);

  useEffect(() => { load(); }, [load]);

  const save = async (recipients) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/admin/otp/egress-alert`, { recipients });
      setSettings(response.data);
      setDraft('');
      toast.success(
        recipients.length
          ? (isRTL ? 'تم تحديث المستلمين' : 'Recipients updated')
          : (isRTL ? 'لن يُرسل هذا التنبيه لأحد' : 'This alert will not be emailed to anyone')
      );
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر الحفظ' : 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const recipients = settings?.recipients || [];

  const add = () => {
    const address = draft.trim().toLowerCase();
    if (!looksLikeEmail(address)) {
      toast.error(isRTL ? 'أدخل بريدًا إلكترونيًا صحيحًا' : 'Enter a valid email address');
      return;
    }
    if (recipients.includes(address)) {
      toast.error(isRTL ? 'موجود بالفعل' : 'Already on the list');
      return;
    }
    save([...recipients, address]);
  };

  return (
    <Card data-testid="egress-alert-recipients">
      <CardHeader>
        <CardTitle className="text-lg">
          {isRTL ? 'من يُبلَّغ عند تغيّر العنوان' : 'Who is told when the address moves'}
        </CardTitle>
        <CardDescription>
          {isRTL
            ? 'يُرسل بريد واحد لكل عنوان جديد، وذلك عند كل إعادة نشر. لن يُرسل لأي شخص غير المدرَجين هنا، ولا يُرسل إن لم يكن هناك أي حساب يستلم رمزه عبر الرسائل النصية.'
            : 'One email per new address, on every redeploy. Nobody but the addresses listed here is ever emailed, and nothing is sent at all unless at least one account would really be sent an SMS code.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!settings ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {recipients.length === 0 ? (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-sm text-amber-900"
                data-testid="egress-alert-empty"
              >
                {isRTL
                  ? 'لا أحد يُبلَّغ حاليًا. إن تغيّر العنوان فلن يعرف أحد، وستتوقف الرسائل النصية بصمت.'
                  : 'Nobody is being told. If the address moves, no one will know and SMS codes will stop arriving silently.'}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="egress-alert-list">
                {recipients.map((address) => (
                  <span
                    key={address}
                    className="inline-flex items-center gap-2 rounded-full border border-brand-beige bg-brand-beige-40 px-3 py-1 text-sm"
                    data-testid={`egress-alert-recipient-${address}`}
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span dir="ltr" className="font-mono text-xs">{address}</span>
                    <button
                      type="button"
                      onClick={() => save(recipients.filter((r) => r !== address))}
                      disabled={saving}
                      className="text-muted-foreground transition-colors hover:text-red-600 disabled:opacity-50"
                      aria-label={isRTL ? 'إزالة' : 'Remove'}
                      data-testid={`egress-alert-remove-${address}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                type="email"
                dir="ltr"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                placeholder={isRTL ? 'name@example.com' : 'name@example.com'}
                disabled={saving || recipients.length >= MAX_RECIPIENTS}
                className="sm:max-w-xs"
                data-testid="egress-alert-input"
              />
              <Button
                type="button"
                onClick={add}
                disabled={saving || !draft.trim() || recipients.length >= MAX_RECIPIENTS}
                className="shrink-0"
                data-testid="egress-alert-add"
              >
                {saving
                  ? <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  : <Plus className="h-4 w-4 me-2" />}
                {isRTL ? 'إضافة' : 'Add'}
              </Button>
            </div>

            <div className="space-y-1 text-xs text-muted-foreground">
              {recipients.length >= MAX_RECIPIENTS && (
                <div data-testid="egress-alert-limit">
                  {isRTL
                    ? `الحد ${MAX_RECIPIENTS} عناوين.`
                    : `The limit is ${MAX_RECIPIENTS} addresses.`}
                </div>
              )}
              <div data-testid="egress-alert-sms-dependents">
                {isRTL
                  ? `الحسابات التي تستلم رمزها عبر الرسائل النصية اليوم: ${settings.sms_dependent_accounts}`
                  : `Accounts that would be sent an SMS code today: ${settings.sms_dependent_accounts}`}
              </div>
              {settings.last_alerted_at && (
                <div data-testid="egress-alert-last">
                  {isRTL ? 'آخر تنبيه: ' : 'Last alerted: '}
                  <span dir="ltr" className="font-mono">{settings.last_alerted_ip}</span>
                  {' · '}
                  {new Date(settings.last_alerted_at).toLocaleString(isRTL ? 'ar-SA' : 'en-GB')}
                  {(settings.last_alerted_to || []).length
                    ? ` · ${settings.last_alerted_to.join(', ')}` : ''}
                </div>
              )}
              {settings.updated_by && (
                <div data-testid="egress-alert-updated-by">
                  {isRTL ? 'آخر تغيير بواسطة ' : 'Last changed by '}
                  {settings.updated_by}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EgressAlertRecipients;
